// Text can arrive HTML-encoded from the backend (e.g. a project named "R&D"
// comes through as "R&amp;D"). React renders text nodes verbatim, so the raw
// entity would be shown to the user. Pure (no DOM) so it is safe to call from
// Redux reducers as well as components. Idempotent on already-decoded text.
// SECURITY: the decoded output is meant for React text nodes only. Because it can
// produce "<", ">" and quotes, it must never be passed to innerHTML /
// dangerouslySetInnerHTML — doing so would reintroduce an XSS sink.
const HTML_NAMED_ENTITIES: Record<string, string> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  nbsp: " ",
};

// C0 controls (0x00–0x1F), DEL and C1 controls (0x7F–0x9F). Decoding these would inject
// control characters (NUL, ESC, ...) into otherwise-plain display text, so leave them encoded.
const isControlCodePoint = (code: number): boolean =>
  code <= 0x1F || (code >= 0x7F && code <= 0x9F);

export function decodeHtmlEntities(text?: string | null): string {
  if (!text || typeof text !== "string") return text ?? "";
  if (!text.includes("&")) return text;
  return text.replace(/&(#x?[0-9a-fA-F]+|[a-zA-Z]+);/g, (match, entity: string) => {
    if (entity[0] === "#") {
      const isHex = entity[1] === "x" || entity[1] === "X";
      const code = parseInt(entity.slice(isHex ? 2 : 1), isHex ? 16 : 10);
      // String.fromCodePoint throws RangeError above U+10FFFF; also skip control code points.
      // Leave anything out of range untouched rather than throwing inside a reducer (AB#1010).
      if (Number.isNaN(code) || code > 0x10FFFF || isControlCodePoint(code)) {
        return match;
      }
      return String.fromCodePoint(code);
    }
    const decoded = HTML_NAMED_ENTITIES[entity.toLowerCase()];
    return decoded !== undefined ? decoded : match;
  });
}
